import { useDispatch, useSelector } from 'react-redux';
import { SubmitHandler, useForm, UseFormWatch, Control } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  formatCurrency,
  calcClientDerivatives,
  appliedHealthCareInflation,
  defaultHealthcareInflation,
  SessionType,
} from '@shared';
import { Label, Switch } from '@design-system';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { LtcCostDisplay } from './LtcCostDisplay';
import { CustomSlider } from './ui/custom-slider';

import {
  selectClient,
  putClientCustomInferencesForClientByClientIdRequest,
  putClientCarePhaseDurationSelectionForClientByClientIdRequest,
  selectClientUpdatesStatus,
  putClientCalculationSettingsByClientIdRequest,
  selectMaybeClientPartner,
  selectSession,
} from '../model';
import { CarePhaseDef, carePhaseDefList, carePhaseDefs } from '@shared';
import {
  FormHeader,
  FormDivider,
  PrimarySecondaryButtonContainer,
  SecondaryFormButton,
  SubmitButton,
} from './FormComponents';
import { FieldErrors } from 'react-hook-form';
import { HealthcareInflationCitation } from './HealthcareInflationCitation';

type FormFields = {
  ltcAtAge: number;
  healthcareInflation: number;
} & Record<CarePhaseDef['key'], number>;

type CarePhaseDurationSelectOptions = 'predicted' | '20' | '200' | 'custom' | '';

type EditCareAssumptionsFormProps = {
  open: boolean;
  setUnsavedChanges: (unsavedChanges: boolean) => void;
};

const durationOptions: {
  value: CarePhaseDurationSelectOptions;
  label: string;
}[] = [
  { value: '20', label: '20% of predicted' },
  { value: 'predicted', label: 'Predicted' },
  { value: '200', label: '200% of predicted' },
  { value: 'custom', label: 'Custom' },
];

const ltcAtAgeValidation = z.coerce.number().int().min(40).max(150);
const careDurationSchema = z.coerce.number().int().min(0).nonnegative();

// Create base schema without ltcAtAge
const baseSchema = {
  healthcareInflation: z.coerce
    .number()
    .min(0, 'Inflation rate must be at least 0%')
    .max(20, 'Inflation rate cannot exceed 20%'),
  ...Object.fromEntries(
    carePhaseDefList.map(carePhaseDef => [
      carePhaseDefs[carePhaseDef.value].key,
      careDurationSchema,
    ]),
  ),
};

function ValidationErrors({ formErrors }: { formErrors: FieldErrors<FormFields> }) {
  return (
    <div className="rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Validation Errors</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <ul className="list-disc space-y-1 pl-5">
              {Object.entries(formErrors).map(([field, error]) => (
                <li key={field}>{error?.message}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function CarePhaseInfo({ label, months, cost }: { label: string; months: number; cost: number }) {
  return (
    <div className="flex flex-1 flex-col items-start">
      <p className="text-xs font-medium text-gray-700">{label}</p>
      <p className="mb-1 flex flex-col text-xs text-gray-600">
        {months} months
        <span className="text-[10px] text-gray-600"> ({(months / 12).toFixed(1)} years)</span>
      </p>
      <p className="text-primary-dark text-sm font-semibold">
        <LtcCostDisplay value={cost} />
      </p>
    </div>
  );
}

function ProjectedLTCCost({ previewClient }: { previewClient: Client }) {
  const phaseLabels = ['Early Care', 'Moderate Care', 'Full Care'];

  return (
    <Card
      className="border-primary/20 sticky top-0 z-10 bg-gray-50 shadow-lg"
      data-testid="projected-ltc-cost-card"
    >
      <CardContent className="flex flex-col p-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="text-sm font-medium text-gray-700">Projected Lifetime LTC Cost</h3>
            <p className="text-xs text-gray-700">Based on current assumptions</p>
          </div>
          <p
            className="text-primary-dark text-2xl font-bold"
            data-testid="projected-ltc-cost-amount"
          >
            <LtcCostDisplay
              value={previewClient.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0}
            />
          </p>
        </div>
        <div className="border-primary/10 mt-4 flex items-center justify-between gap-2 border-t pt-3">
          {phaseLabels.map((label, index) => (
            <CarePhaseInfo
              key={label}
              label={label}
              months={previewClient.phaseCosts?.[index]?.phaseCareMonthsNeeded || 0}
              cost={previewClient.phaseCosts?.[index]?.phaseInflatedProfessionalShareCost || 0}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface HealthcareInflationSectionProps {
  control: Control<FormFields>;
  watch: UseFormWatch<FormFields>;
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
}

function HealthcareInflationSection({
  control,
  watch,
  previewClient,
  updatePreviewClient,
}: HealthcareInflationSectionProps) {
  return (
    <div
      className="space-y-2"
      data-testid="healthcare-inflation-section"
    >
      <FormHeader title="Inflation Rate" />
      <label className="text-sm text-gray-700">
        Annual Healthcare Inflation Rate:{' '}
        <span data-testid="healthcare-inflation-value">
          {parseFloat(watch('healthcareInflation').toFixed(1))}%
        </span>
      </label>
      <div className="my-6 flex flex-col gap-4">
        <CustomSlider
          name="healthcareInflation"
          control={control}
          min={0}
          max={20}
          step={0.1}
          defaultValue={defaultHealthcareInflation(previewClient, true)}
          labelSuffix="%"
          defaultLabel="Default"
          formatValue={value => parseFloat(value.toFixed(1))}
          data-testid="healthcare-inflation-slider"
          updateClient={value => {
            const updatedClient = {
              ...previewClient,
              clientCalculationSettings: {
                ...previewClient.clientCalculationSettings,
                healthCareInflationRate: value / 100,
              },
            };
            updatePreviewClient(updatedClient);
          }}
        />
      </div>
      <HealthcareInflationCitation
        client={previewClient}
        className="mt-2 italic"
      />
    </div>
  );
}

interface CareStartAgeSectionProps {
  control: Control<FormFields>;
  watch: UseFormWatch<FormFields>;
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
  predictedLtcAtAge: number;
}

function CareStartAgeSection({
  control,
  watch,
  previewClient,
  updatePreviewClient,
  predictedLtcAtAge,
}: CareStartAgeSectionProps) {
  const ltcAtAgeValue = watch('ltcAtAge');

  const clientCurrentAge = previewClient.intakeSurvey.clientAge;
  const minAge = clientCurrentAge + 1;

  return (
    <div
      className="space-y-2"
      data-testid="care-start-age-section"
    >
      <FormHeader
        title="When will care start?"
        tooltipContent={`The estimated age when long-term care (LTC) will begin. `}
      />
      <div className="flex flex-col gap-1 text-sm text-gray-600">
        <p>
          Start Age: <span data-testid="care-start-age-value">{ltcAtAgeValue} years old</span>
        </p>
        <p>
          End Age:{' '}
          <span data-testid="care-end-age-value">
            {Math.round(
              ltcAtAgeValue +
                Number(previewClient.allPhaseCosts?.allPhaseCareMonthsNeeded ?? 0) / 12,
            )}{' '}
            years old
          </span>
        </p>
      </div>
      <div className="my-6 flex flex-col gap-4">
        <CustomSlider
          name="ltcAtAge"
          control={control}
          min={minAge}
          max={150}
          defaultValue={predictedLtcAtAge}
          formatValue={value => `${value} years old`}
          defaultLabel="Predicted"
          data-testid="care-start-age-slider"
          updateClient={value => {
            const updatedClient = {
              ...previewClient,
              clientCustomInferences: {
                ...previewClient.clientCustomInferences,
                ltcAtAge: value,
              },
            };
            updatePreviewClient(updatedClient);
          }}
        />
      </div>
    </div>
  );
}

interface CarePhaseDurationsSectionProps {
  control: Control<FormFields>;
  watch: UseFormWatch<FormFields>;
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
  selectedPercentage: CarePhaseDurationSelectOptions;
  setSelectedPercentage: (value: CarePhaseDurationSelectOptions) => void;
  handleChangeCarePhaseDurationSelection: (percentage: number | 'predicted' | 'custom') => void;
  predictedDuration: (carePhaseDef: CarePhaseDef) => number;
  isPastPhase: (phaseIndex: number) => boolean;
}

function CarePhaseDurationsSection({
  control,
  watch,
  previewClient,
  updatePreviewClient,
  selectedPercentage,
  setSelectedPercentage,
  handleChangeCarePhaseDurationSelection,
  predictedDuration,
  isPastPhase,
}: CarePhaseDurationsSectionProps) {
  return (
    <div
      className="space-y-2"
      data-testid="care-phase-duration-section"
    >
      <FormHeader title="How long will care last?" />

      {/* Toggle Group */}
      <ToggleGroup
        type="single"
        className="mb-4 flex w-full gap-0 [&>*]:flex-1"
        value={selectedPercentage}
        variant="outline"
        data-testid="care-phase-duration-toggle"
        onValueChange={value => {
          // Only update if a new value is selected (prevents deselection)
          if (value) {
            if (value === 'custom') {
              setSelectedPercentage('custom');
            } else {
              setSelectedPercentage(value as CarePhaseDurationSelectOptions);
              handleChangeCarePhaseDurationSelection(
                value === 'predicted' ? 'predicted' : parseInt(value),
              );
            }
          }
        }}
      >
        {durationOptions.map((option, index) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className={`text-xs text-gray-900 hover:text-gray-900 ${
              index === 0 ? 'rounded-r-none' : 'rounded-none border-l-0'
            } ${index === durationOptions.length - 1 ? 'rounded-l-none rounded-r-md' : ''}`}
            style={{
              fontSize: '12px',
              paddingLeft: '5px',
              paddingRight: '5px',
            }}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Care phase sliders - always visible but only interactive in custom mode */}
      <div className="mb-4">
        {carePhaseDefList.map((carePhaseDef, index) => {
          const phasePredictedDuration = predictedDuration(carePhaseDef);
          const phaseKey = carePhaseDefs[carePhaseDef.value].key;
          const isCustomMode = selectedPercentage === 'custom';

          return (
            <div
              key={carePhaseDef.key}
              className={`mb-4 ${isPastPhase(carePhaseDef.index) ? 'hidden' : ''}`}
            >
              <label className="text-sm text-gray-700">
                {carePhaseDef.label}:{' '}
                <span id={`${carePhaseDef.key}-value`}>{watch(phaseKey)}</span> months (
                {(watch(phaseKey) / 12).toFixed(1)} years)
                {!isCustomMode && (
                  <span className="ml-2 text-xs italic text-gray-500">
                    (Select "Custom" to edit)
                  </span>
                )}
              </label>
              <div className={!isCustomMode ? 'pointer-events-none opacity-40' : ''}>
                <CustomSlider
                  name={phaseKey}
                  control={control}
                  min={0}
                  max={Math.max(120, phasePredictedDuration * 2)}
                  defaultValue={phasePredictedDuration}
                  formatValue={value => `${value} months (${(value / 12).toFixed(1)} years)`}
                  defaultLabel="Predicted"
                  hideResetButton={!isCustomMode}
                  data-testid={`care-phase-${carePhaseDef.slug}-slider`}
                  updateClient={value => {
                    // Update the label text directly
                    const label = document.getElementById(`${carePhaseDef.key}-value`);
                    if (label) {
                      label.textContent = value.toString();
                    }

                    // Update preview client locally
                    const updatedCarePhaseDurations = {
                      ...Object.fromEntries(
                        carePhaseDefList.map(phaseDef => [
                          phaseDef.value,
                          watch(carePhaseDefs[phaseDef.value].key),
                        ]),
                      ),
                      [carePhaseDef.value]: value,
                    };
                    const updatedClient = {
                      ...previewClient,
                      carePhaseDurationSelections: updatedCarePhaseDurations,
                    };
                    updatePreviewClient(updatedClient);
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Internal, advisor-only toggle (visibility gated at the call site by the
// `claimProjectionEngine` feature flag) selecting the funding calculation
// engine. Writes `clientCalculationSettings.useClaimProjectionEngine`, which the
// funding calc reads in `runPolicyCalculations`. Updating the preview client
// recomputes the slide-out's projected-cost preview with the chosen engine
// immediately; the dispatch persists the setting so server-side calcs agree.
function CalculationEngineSection({
  previewClient,
  updatePreviewClient,
}: {
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
}) {
  const dispatch = useDispatch();
  const checked = previewClient.clientCalculationSettings?.useClaimProjectionEngine ?? false;

  const handleToggle = (next: boolean) => {
    updatePreviewClient({
      ...previewClient,
      clientCalculationSettings: {
        ...previewClient.clientCalculationSettings,
        useClaimProjectionEngine: next,
      },
    });
    dispatch(
      putClientCalculationSettingsByClientIdRequest({
        clientId: previewClient.clientId,
        clientCalculationSettings: { useClaimProjectionEngine: next },
      }),
    );
  };

  return (
    <div
      className="space-y-2"
      data-testid="calculation-engine-section"
    >
      <FormHeader title="Calculation Engine" />
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label
            htmlFor="calculation-engine-toggle"
            className="text-sm text-gray-700"
          >
            Use new month-based projection engine
          </Label>
          <p className="text-xs italic text-gray-500">
            Internal: switches funding projections from the legacy phase-based calc to the new
            month-based claim-projection engine. Off uses the legacy engine.
          </p>
        </div>
        <Switch
          id="calculation-engine-toggle"
          data-testid="calculation-engine-toggle"
          checked={checked}
          onCheckedChange={handleToggle}
        />
      </div>
    </div>
  );
}

export function EditCareAssumptionsForm({ open, setUnsavedChanges }: EditCareAssumptionsFormProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const { sessionType } = useSelector(selectSession);
  // Advisor/wizard-only, behind the claimProjectionEngine flag.
  const showEngineToggle =
    !!flags.claimProjectionEngine &&
    (sessionType === SessionType.advisor || sessionType === SessionType.wizard);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientStatus = useSelector(selectClientUpdatesStatus);
  const isLoading =
    clientStatus.healthCareInflationRate === 'loading' ||
    clientStatus.phaseDurations === 'loading' ||
    clientStatus.ltcAtAge === 'loading';

  const {
    intakeSurvey: { clientHasStartedLtc, clientCurrentCarePhase },
    appliedInferenceSet: { ltcAtAge, ltcDurationYears },
    carePhaseDurationSelections = {}, // Provide default empty object
    phaseCosts = [], // Provide default empty array
  } = client;

  // Ensure currentCarePhaseIndex is valid
  const currentCarePhaseIndex = carePhaseDefList.findIndex(
    carePhaseDef => carePhaseDef.value === clientCurrentCarePhase,
  );

  const isPastPhase = (phaseIndex: number) =>
    phaseIndex < (currentCarePhaseIndex === -1 ? 0 : currentCarePhaseIndex);

  // Initialize the preview client state with the current client
  const [previewClient, setPreviewClient] = useState<Client>(client);

  // Add state to track if form has been reset
  const [hasBeenReset, setHasBeenReset] = useState(false);

  // calculate the default care phase duration selection based on carePhaseDurationSelections
  const predictedDuration = (carePhaseDef: CarePhaseDef) => {
    if (!phaseCosts || !phaseCosts[carePhaseDef.index] || !ltcDurationYears) {
      return 0;
    }
    return Math.round(phaseCosts[carePhaseDef.index].phaseDurationRatio * ltcDurationYears * 12);
  };
  const determineSelectedPercentage = (): CarePhaseDurationSelectOptions => {
    // If no selections exist, return predicted
    if (Object.keys(carePhaseDurationSelections).length === 0) {
      return 'predicted';
    }

    // Check if all durations match predicted values
    const allPredicted = carePhaseDefList.every(
      carePhaseDef =>
        carePhaseDurationSelections[carePhaseDef.value] === predictedDuration(carePhaseDef),
    );
    if (allPredicted) return 'predicted';

    // Check for 20% of predicted and 200% of predicted
    // Check if values match 20% of predicted
    const matches20Percent = carePhaseDefList.every(carePhaseDef => {
      const predicted = predictedDuration(carePhaseDef);
      const actual = carePhaseDurationSelections[carePhaseDef.value];
      const expected = Math.round(predicted * 0.2); // 20% of predicted
      return actual === expected;
    });
    if (matches20Percent) return '20';

    // Check if values match 200% of predicted
    const matches200Percent = carePhaseDefList.every(carePhaseDef => {
      const predicted = predictedDuration(carePhaseDef);
      const actual = carePhaseDurationSelections[carePhaseDef.value];
      const expected = Math.round(predicted * 2); // 200% of predicted
      return actual === expected;
    });
    if (matches200Percent) return '200';

    // If no standard percentage matches, it's a custom value
    return 'custom';
  };
  const [selectedPercentage, setSelectedPercentage] = useState<CarePhaseDurationSelectOptions>(
    determineSelectedPercentage(),
  );

  const predictedLtcAtAge = client.inferenceSet?.ltcAtAge ?? 65; // Provide default value

  // Conditionally add ltcAtAge validation based on clientHasStartedLtc
  const schema = z.object(
    clientHasStartedLtc
      ? baseSchema
      : {
          ...baseSchema,
          ltcAtAge: ltcAtAgeValidation,
        },
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors: formErrors, isDirty },
  } = useForm<FormFields>({
    defaultValues: {
      ltcAtAge: client.clientCustomInferences.ltcAtAge ?? predictedLtcAtAge,
      healthcareInflation: parseFloat((appliedHealthCareInflation(client) * 100).toFixed(1)),
      ...Object.fromEntries(
        carePhaseDefList.map(carePhaseDef => [
          carePhaseDefs[carePhaseDef.value].key,
          carePhaseDurationSelections[carePhaseDef.value] ?? predictedDuration(carePhaseDef),
        ]),
      ),
    },
    resolver: zodResolver(schema),
  });

  const updatePreviewClient = (updatedClient: Client) => {
    const updatedClientWithDerivatives = calcClientDerivatives(updatedClient, clientPartner);
    setPreviewClient(updatedClientWithDerivatives as Client);
  };

  const handleChangeCarePhaseDurationSelection = (percentage: number | 'predicted' | 'custom') => {
    if (percentage === 'custom') {
      setSelectedPercentage('custom');
      return;
    }

    const updatedCarePhaseDurations: Record<CarePhase, number> = {} as Record<CarePhase, number>;
    carePhaseDefList.forEach(carePhaseDef => {
      const baseValue = predictedDuration(carePhaseDef);
      const newValue =
        percentage === 'predicted'
          ? baseValue
          : percentage === 20
            ? Math.round(baseValue * 0.2) // 20% of predicted
            : Math.round(baseValue * (percentage / 100)); // 200% of predicted (2x total)
      setValue(carePhaseDefs[carePhaseDef.value].key, newValue, {
        shouldDirty: true,
      });
      updatedCarePhaseDurations[carePhaseDef.value] = newValue;

      // Update the label text directly
      const label = document.getElementById(`${carePhaseDef.key}-value`);
      if (label) {
        label.textContent = newValue.toString();
      }
    });

    const updatedClient = {
      ...previewClient,
      carePhaseDurationSelections: updatedCarePhaseDurations,
    };
    updatePreviewClient(updatedClient);
  };

  const handleReset = () => {
    setSelectedPercentage('predicted');
    setHasBeenReset(true);

    // Create reset values
    const resetCarePhaseDurations: Record<CarePhase, number> = {} as Record<CarePhase, number>;
    carePhaseDefList.forEach(carePhaseDef => {
      resetCarePhaseDurations[carePhaseDef.value] = predictedDuration(carePhaseDef);
    });

    // Reset form with predicted/default values using setValue to mark as dirty
    if (!clientHasStartedLtc) {
      setValue('ltcAtAge', predictedLtcAtAge, { shouldDirty: true });
    }
    setValue(
      'healthcareInflation',
      parseFloat(defaultHealthcareInflation(previewClient, true).toFixed(1)),
      {
        shouldDirty: true,
      },
    );

    carePhaseDefList.forEach(carePhaseDef => {
      setValue(carePhaseDefs[carePhaseDef.value].key, predictedDuration(carePhaseDef), {
        shouldDirty: true,
      });

      // Update the label text directly
      const label = document.getElementById(`${carePhaseDef.key}-value`);
      if (label) {
        label.textContent = predictedDuration(carePhaseDef).toString();
      }
    });

    // Update preview client locally instead of dispatching to Redux
    const updatedClient = {
      ...client,
      appliedInferenceSet: {
        ...client.appliedInferenceSet,
        ltcAtAge: predictedLtcAtAge,
      },
      clientCustomInferences: {
        ...client.clientCustomInferences,
        ltcAtAge: predictedLtcAtAge,
      },
      carePhaseDurationSelections: resetCarePhaseDurations,
      clientCalculationSettings: {
        ...client.clientCalculationSettings,
        healthCareInflationRate: defaultHealthcareInflation(previewClient),
      },
    };
    updatePreviewClient(updatedClient);
  };

  const isAllPredictedValues = () => {
    try {
      // Check LTC age
      if (!clientHasStartedLtc && watch('ltcAtAge') !== predictedLtcAtAge) {
        return false;
      }

      // Check healthcare inflation
      if (
        parseFloat(watch('healthcareInflation').toFixed(1)) !==
        parseFloat(defaultHealthcareInflation(previewClient, true).toFixed(1))
      ) {
        return false;
      }

      // Check all care phase durations
      return carePhaseDefList.every(
        carePhaseDef =>
          watch(carePhaseDefs[carePhaseDef.value].key) === predictedDuration(carePhaseDef),
      );
    } catch (err) {
      console.error('Error checking if all values are predicted:', err);
      return false;
    }
  };

  const onSubmit: SubmitHandler<FormFields> = data => {
    if (!clientHasStartedLtc) {
      dispatch(
        putClientCustomInferencesForClientByClientIdRequest({
          clientId: client.clientId,
          clientCustomInferences: {
            ltcAtAge: data.ltcAtAge,
          },
        }),
      );
    }

    dispatch(
      putClientCarePhaseDurationSelectionForClientByClientIdRequest({
        clientId: client.clientId,
        carePhaseDurationSelections: Object.fromEntries(
          carePhaseDefList.map(carePhaseDef => [
            carePhaseDef.value,
            data[carePhaseDef.key],
          ]),
        ),
      }),
    );

    dispatch(
      putClientCalculationSettingsByClientIdRequest({
        clientId: client.clientId,
        clientCalculationSettings: {
          healthCareInflationRate: data.healthcareInflation / 100,
        },
      }),
    );
  };

  // reset form on open side effects
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedPercentage(determineSelectedPercentage());
      setPreviewClient(client);
      setHasBeenReset(false);
    }
  }, [open, client]);

  // Move this state update to useEffect instead of during render
  useEffect(() => {
    if (isDirty && !isLoading) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [isDirty, isLoading, setUnsavedChanges]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      data-testid="edit-care-assumptions-form"
    >
      {/* Display form validation errors if any */}
      {Object.keys(formErrors).length > 0 && <ValidationErrors formErrors={formErrors} />}

      {/* Projected LTC Cost Card */}
      <ProjectedLTCCost previewClient={previewClient} />

      <HealthcareInflationSection
        control={control}
        watch={watch}
        previewClient={previewClient}
        updatePreviewClient={updatePreviewClient}
      />

      <FormDivider />

      {/* Care Start Age - Only show if care hasn't started */}
      {!clientHasStartedLtc && (
        <>
          <CareStartAgeSection
            control={control}
            watch={watch}
            previewClient={previewClient}
            updatePreviewClient={updatePreviewClient}
            predictedLtcAtAge={predictedLtcAtAge}
          />
          <FormDivider />
        </>
      )}

      {/* Care Phase Durations */}
      <CarePhaseDurationsSection
        control={control}
        watch={watch}
        previewClient={previewClient}
        updatePreviewClient={updatePreviewClient}
        selectedPercentage={selectedPercentage}
        setSelectedPercentage={setSelectedPercentage}
        handleChangeCarePhaseDurationSelection={handleChangeCarePhaseDurationSelection}
        predictedDuration={predictedDuration}
        isPastPhase={isPastPhase}
      />

      {showEngineToggle && (
        <>
          <FormDivider />
          <CalculationEngineSection
            previewClient={previewClient}
            updatePreviewClient={updatePreviewClient}
          />
        </>
      )}

      <PrimarySecondaryButtonContainer className="md:flex-row">
        <SecondaryFormButton
          label="Reset to Predicted"
          onClick={handleReset}
          disabled={isLoading || isAllPredictedValues()}
          data-testid="care-assumptions-reset-button"
        />
        <SubmitButton
          label={isLoading ? 'Saving...' : 'Save'}
          disabled={(!isDirty && !hasBeenReset) || isLoading}
          isLoading={isLoading}
          className="px-6"
          data-testid="care-assumptions-save-button"
        />
      </PrimarySecondaryButtonContainer>
    </form>
  );
}
